/******/ (() => { // webpackBootstrap
/* eslint-disable */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.cacheDOMElements();
    /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
    this.onSubmitBound = this.onSubmit.bind(this);
    this.handleQuickViewModal = this.handleQuickViewModal.bind(this);
    this.validationErrors = new Map();
    this.onValidationInputBound = this.onValidationInput.bind(this);
  }

  cacheDOMElements() {
    /* ===== Cache the DOM elements ===== */
    this.form = this.querySelector('form');
    this.sectionId = this.getAttribute('data-section-id');
    this.productInformation = this.closest('product-information');
    this.cartAction = document.getElementById('PageContainer').dataset.cartAction;
    this.cartType = document.getElementById('PageContainer').dataset.cartType;
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.giftWrapProductId = this.getAttribute('data-gift-wrap-product-id');
    this.wethemeGlobal = document.querySelector('script#wetheme-global');
    this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
    this.qtyErrorMessage = this.querySelector('[data-qty-error-message]');
    this.statusRegion = this.querySelector('[data-product-form-status]');
    this.blankElements = [];
  }

  connectedCallback() {
    /* ===== Set the initial state of the add to cart button ===== */
    if (!this.form) return;
    this.attachEventListeners();
  }

  attachEventListeners() {
    /* ===== Attach the event listeners to the DOM elements ===== */
    this.form.addEventListener('submit', this.onSubmitBound);
    this.productInformation.addEventListener('input', this.onValidationInputBound);
    this.productInformation.addEventListener('change', this.onValidationInputBound);
  }

  onSubmit(event) {
    /* ===== Handle the form submission ===== */
    event.preventDefault();
    this.prepareForSubmission();
  }

  prepareForSubmission() {
    /* ===== Prepare the form for submission ===== */
    this.cacheSubmissionElements();
    if (!this.checkRequiredInputs()) return;
    if (!this.checkPurchasableSelection()) return;
    this.clearSubmissionMessages();
    this.disableBlankInputs();
    // If the form is valid, proceed with the submission
    this.showLoadingState();
    this.addItemsToCart();
  }

  cacheSubmissionElements() {
    /* ===== Cache the elements needed for form submission ===== */
    this.loadingIcon = this.form.querySelector('[data-loading-icon]');
    this.loadingText = this.form.querySelector('[data-loading-text]');
    this.addToCartText = this.form.querySelector('[data-add-to-cart-text]');
    this.addedText = this.form.querySelector('[data-cart-added-text]');
    this.quantityInput = this.form.querySelector('[name="quantity"]');
    this.quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
  }

  checkPurchasableSelection() {
    const variantInput = this.form.querySelector('[name="id"]');
    const addButton = this.form.querySelector('[name="add"]');
    if (variantInput && !variantInput.disabled && variantInput.value && addButton && !addButton.disabled) return true;

    this.showStatus(this.statusRegion?.dataset.unavailableMessage, 'error');
    addButton?.focus();
    return false;
  }

  clearSubmissionMessages() {
    if (this.qtyErrorMessage) {
      this.qtyErrorMessage.classList.add('hidden');
      this.syncQuantityErrorDescription(false);
    }
    this.showStatus('', '');
  }

  syncQuantityErrorDescription(show) {
    const addButton = this.form.querySelector('[name="add"]');
    const quantityErrorId = this.qtyErrorMessage?.id;
    if (!addButton || !quantityErrorId) return;

    const descriptionIds = new Set((addButton.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    show ? descriptionIds.add(quantityErrorId) : descriptionIds.delete(quantityErrorId);

    if (descriptionIds.size) {
      addButton.setAttribute('aria-describedby', [...descriptionIds].join(' '));
    } else {
      addButton.removeAttribute('aria-describedby');
    }
  }

  disableBlankInputs() {
    // Select all input and textarea elements
    const elements = this.productInformation.querySelectorAll('input, textarea');
    this.blankElements = [];

    elements.forEach((element) => {
      // Only restore controls disabled by this submission, never hidden fields.
      if (!element.disabled && !element.value) {
        // Temporarily disable the element so that it is not included in the form submission
        element.disabled = true;
        this.blankElements.push(element);
      }
    });
  }

  enableBlankInputs() {
    // Enable the blank elements that were disabled
    (this.blankElements || []).forEach((element) => {
      element.disabled = false;
    });
    this.blankElements = [];
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  checkRequiredInputs() {
    // Product forms use novalidate; enforce native constraints as well as the
    // theme's required custom-control checks before any cart request.
    const requiredInputs = Array.from(this.productInformation.querySelectorAll('[required]:not(:disabled):not([data-input-ignore])'));
    this.clearErrorMessages();

    const invalidInputs = requiredInputs.filter(input => !this.isInputValid(input));
    invalidInputs.forEach(input => this.applyErrorMessage(input));
    invalidInputs[0]?.focus();
    return invalidInputs.length === 0;
  }

  isInputValid(input) {
    if (input.disabled || input.hasAttribute('data-input-ignore')) return true;
    if (input.required && (input.type === 'checkbox' ? !input.checked : !input.value.trim())) return false;
    return !input.validity || input.validity.valid;
  }

  onValidationInput() {
    // Also clears recipient errors when its checkbox hides/disables the fields.
    for (const input of this.validationErrors.keys()) {
      if (this.isInputValid(input)) this.clearInputError(input);
    }
  }

  clearErrorMessages() {
    for (const input of this.validationErrors.keys()) this.clearInputError(input);
  }

  clearInputError(input) {
    const record = this.validationErrors.get(input);
    if (!record) return;
    record.element.remove();
    const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== record.element.id);
    ids.length ? input.setAttribute('aria-describedby', ids.join(' ')) : input.removeAttribute('aria-describedby');
    record.previousInvalid === null ? input.removeAttribute('aria-invalid') : input.setAttribute('aria-invalid', record.previousInvalid);
    this.validationErrors.delete(input);
  }

  applyErrorMessage(input) {
    if (!input) return;
    this.clearInputError(input);
    const translations = this.translationsObject.translations;
    const requiredMessage = input.hasAttribute('custom-dropdown-input') ? translations.required_dropdown_error : input.type === 'file' ? translations.required_upload_error : input.type === 'checkbox' ? translations.required_checkbox_error : translations.required_input_error;
    const missing = input.required && (input.type === 'checkbox' ? !input.checked : !input.value.trim());
    const message = (missing ? requiredMessage : input.validationMessage) || requiredMessage || 'Please check this field.';
    const error = document.createElement('span');
    const baseId = `${input.id || this.form.id || 'product-field'}-validation-error`;
    let errorId = baseId;
    for (let suffix = 1; document.getElementById(errorId); suffix++) errorId = `${baseId}-${suffix}`;
    error.id = errorId;
    error.className = 'required-field-error errors';
    error.style.display = 'block';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    const errorContainer = input.closest('.input-wrapper')?.querySelector('.input-error-container');
    errorContainer ? errorContainer.append(error) : input.insertAdjacentElement('afterend', error);
    this.validationErrors.set(input, { element: error, previousInvalid: input.getAttribute('aria-invalid') });
    const descriptionIds = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    descriptionIds.add(error.id);
    input.setAttribute('aria-describedby', [...descriptionIds].join(' '));
    input.setAttribute('aria-invalid', 'true');
  }

  giftWrapEnabled() {
    /* ===== Determine if the gift wrap product is enabled ===== */
    const giftWrapProp = this.closest('product-information')?.getAttribute('data-gift-wrap-prop');
    if (!giftWrapProp || !this.giftWrapProductId) return false;
    const name = `properties[${giftWrapProp}]`;
    const selector = `[name="${CSS.escape(name)}"]`;
    return !!this.productInformation.querySelector(selector)?.checked;
  }

  async addItemsToCart() {
    /* ===== Add the item(s) to the cart ===== */
    try {
      // FormData snapshots successful controls. Restore temporary omissions before
      // awaiting the request so recipient toggles/reset retain their own state.
      const config = this.fetchConfigWithBody();
      this.enableBlankInputs();
      const mainProductAdded = await this.addProductToCart(routes.cart_add_url, config);
      if (mainProductAdded.ok && this.giftWrapEnabled()) {
        // Add the gift wrap product to the cart
        await this.addProductToCart(routes.cart_add_url, this.additionalProductConfig(this.giftWrapProductId, this.quantity));
      }
      // Process the response
      await this.processResponse(mainProductAdded);
    } catch (error) {
      this.handleSubmissionFailure();
    } finally {
      this.enableBlankInputs();
    }
  }

  async addProductToCart(url, config) {
    /* ===== Add the product to the cart ===== */
    const response = await fetch(url, config);
    if (response.ok) {
      // Update cart drawer
      if (this.cartType === 'drawer') {
        const responseJson = await response.json();
        this.updateCartDrawer(responseJson);
      }
    }
    return response;
  }

  additionalProductConfig(productId, quantity) {
    /* ===== Create the configuration object for the additional product(s) to be added to the cart ===== */
    const additionalItemPayload = { items: [{ id: productId, quantity: quantity }] };
    if (this.cartType === 'drawer') {
      additionalItemPayload['sections'] = 'cart-drawer';
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(additionalItemPayload)
    };
  }

  updateCartDrawer(responseJson) {
    eventBus.emit('update:cart:drawer', responseJson);
  }

  async getCart() {
    /* ===== Fetch the cart information ===== */
    try {
      const response = await fetch(`${routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.description || error.message}`);
    }
  }

  updateCartCountIndicator() {
    /* ===== Update the cart count indicator ===== */
    if (!this.cartCountIndicator) return;
    this.getCart().then(cart => window.wetheme.updateCartCount(cart));
  }

  fetchConfigWithBody() {
    /* ===== Create the fetch configuration object with the form data as the body ===== */
    const config = this.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = this.addSectionsParam(new FormData(this.form));
    return config;
  }

  addSectionsParam(formData) {
    /* ===== Add the sections parameter to the form data for cart drawer rendering ===== */
    if (this.cartType === 'drawer') {
      formData.append('sections', 'cart-drawer');
    }
    return formData;
  }

  async processResponse(response) {
    /* ===== Process the response from the server ===== */
    if (!response.ok) {
      const errorMessage = await this.readErrorMessage(response);
      this.handleSubmissionFailure(errorMessage, response.status === 422);
      return;
    }

    this.hideLoadingState();
    this.showStatus(this.statusRegion?.dataset.successMessage, 'success');

    // Emit the cart:added event
    eventBus.emit('cart:added', { sectionId: this.sectionId });
    
    // Handle quick view modal if we're in one
    this.handleQuickViewModal();
    
  }

  async readErrorMessage(response) {
    // Only cart validation responses contain useful customer-facing details.
    if (response.status !== 422) return '';
    try {
      const responseBody = await response.clone().json();
      const message = responseBody?.description || responseBody?.message;
      return typeof message === 'string' ? message.trim() : '';
    } catch (error) {
      return '';
    }
  }

  handleSubmissionFailure(message, isQuantityError = false) {
    message = (typeof message === 'string' && message.trim()) || this.statusRegion?.dataset.errorMessage
      || "We couldn't confirm this item was added. Check your cart before trying again.";
    this.hideLoadingState(false);
    if (isQuantityError) {
      window.eventBus.emit('render:cart:drawer');
      this.updateCartCountIndicator();
      if (this.qtyErrorMessage) {
        const quantityErrorText = this.qtyErrorMessage.querySelector('span');
        if (quantityErrorText && message) quantityErrorText.textContent = message;
        this.qtyErrorMessage.classList.remove('hidden');
        this.syncQuantityErrorDescription(true);
      }
      this.showStatus('', '');
      return;
    }
    this.showStatus(message || this.statusRegion?.dataset.errorMessage, 'error');
  }

  showStatus(message = '', type = '') {
    if (!this.statusRegion) return;
    this.statusRegion.textContent = message;
    type ? this.statusRegion.setAttribute('data-status-type', type) : this.statusRegion.removeAttribute('data-status-type');
  }

  openCartDrawer() {
    /* ===== Open the cart drawer ===== */
    eventBus.emit('open:cart:drawer', {
      scrollToTop: true,
      returnFocusElement: this.form.querySelector('[name="add"]'),
    });
  }

  handleQuickViewModal() {
    /* ===== Close the quick view modal if product was added from quick view ===== */
    if (this.closest('[data-quick-view-product]')) {
      const closeModal = () => {
        const isModalOpen = document.body.classList.contains('theme-modal-open');
        if (isModalOpen) {
          eventBus.emit('theme:modal:hide');
        }
      };

      if (this.cartAction === 'show_added_message' && this.addedText) {
        setTimeout(closeModal, 1500); // Allows the 'added' message to display before closing the modal
      } else {
        closeModal();
      }
    }
  }

  showLoadingState() {
    /* ===== Show the loading state of the add to cart button ===== */
    this.toggleElements(this.addToCartText, false);
    this.toggleElements(this.loadingIcon, true, 'hidden');
    this.toggleElements(this.loadingText, true, 'hidden');
    this.toggleAddButton(true);
    this.form.querySelector('[name="add"]')?.setAttribute('aria-busy', 'true');
  }

  hideLoadingState(success = true) {
    /* ===== Hide the loading state of the add to cart button ===== */
    this.toggleElements(this.loadingIcon, false, 'hidden');
    this.toggleElements(this.loadingText, false, 'hidden');
    this.form.querySelector('[name="add"]')?.removeAttribute('aria-busy');
    if (success) {
      this.toggleCartActionDisplay();
    } else {
      this.toggleAddToCartText(true);
      this.toggleAddButton(false);
    }
  }

  toggleCartActionDisplay() {
    /* ===== Toggle the cart action display based on the selected action ===== */
    if (this.cartAction === 'show_added_message' && this.addedText) {
      // Show the added text message and update the cart count indicator
      this.addedText.style.display = 'block';
      setTimeout(() => {
        this.toggleAddToCartText(true);
        this.toggleAddButton(false); 
        this.updateCartCountIndicator();
      }, 2000);
    } else if (this.cartType === 'drawer' && this.cartAction === 'go_to_or_open_cart') {
      // Open the cart drawer 
      this.toggleAddToCartText(true);
      this.openCartDrawer();
      this.toggleAddButton(false);
      this.updateCartCountIndicator();
    } else {
      // Redirect to the cart page
      this.toggleAddToCartText(true);
      this.toggleAddButton(false);
      window.location = window.routes.cart_url;
    }
  }

  toggleAddToCartText(show) {
    /* ===== Toggle the add to cart text and the added text message ===== */
    // Show the added text message and hide the add to cart text
    if (this.addedText) this.addedText.style.display = show ? 'none' : 'block';
    // Show the add to cart text and hide the added text message
    if (this.addToCartText) this.addToCartText.style.display = show ? 'block' : 'none';
  }

  toggleElements(element, show, className) {
    /* ===== Toggle the display of the element based on the show value ===== */
    if (!element) return;
    if (className) {
      show ? element.classList.remove(className) : element.classList.add(className);
    } else {
      element.style.display = show ? 'block' : 'none';
    }
  }

  toggleAddButton(disable = true) {
    /* ===== Toggle the state of the add to cart button ===== */
    const addButton = this.querySelector('[name="add"]');
    if (!addButton) return;

    addButton.disabled = disable;
  }

  disconnectedCallback() {
    /* ===== Remove the event listeners when the element is removed from the DOM ===== */
    this.form?.removeEventListener('submit', this.onSubmitBound);
    this.productInformation?.removeEventListener('input', this.onValidationInputBound);
    this.productInformation?.removeEventListener('change', this.onValidationInputBound);
    this.clearErrorMessages();
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

/******/ })()
;
